import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, shortId } from '@/shared/lib/format'
import type { SurveyBackCheckDto } from '@/shared/api/types'
import {
  acceptResponse, getQualityQueue, listBackChecks, listPolls, rejectResponse,
} from './api'
import { PollSelect } from './PollSelect'
import { ResponseDrawer } from './ResponseDrawer'
import { BackCheckDialog } from './BackCheckDialog'
import { InterviewerPanel } from './InterviewerPanel'
import { Pill, count, duration, flagRuleLabel, flagSeverityTone } from './vocab'

/** How many interviewer records the queue renders before it stops fetching. */
const INTERVIEWER_CARDS = 8

/**
 * The Field Quality queue.
 *
 * ## The histogram matters as much as the queue
 *
 * A rule firing on ninety per cent of interviews is a mis-tuned threshold, not a
 * fraud wave — and a screen that only lists items cannot tell you that. So the
 * rule counts sit above the queue, and reading them is the first thing a
 * supervisor should do.
 *
 * ## Nothing here deletes anything
 *
 * A flag sets the interview to FLAGGED and takes it out of the estimates. Only a
 * person rejects one, with a written reason, and the interview STAYS — a rejected
 * interview that vanished would make the AAPOR denominators wrong in the
 * flattering direction.
 *
 * PollSphere's `FLAGGED` is a status the client supplies; nothing on its server
 * ever flags anything, and there is no queue because there is nothing to queue.
 *
 * ## The three things the queue was missing
 *
 * Listing flags and resolving them is not a workflow. A supervisor cannot decide
 * an interview without seeing what was answered (`ResponseDrawer`), cannot verify
 * a detection without recording what the re-contact found (`BackCheckDialog`),
 * and cannot judge a pattern without the interviewer's own record
 * (`InterviewerPanel`). Those three are what turn a list of complaints into work
 * somebody can finish.
 */
export function QualityPage() {
  const canRead = useCan('survey:read')
  const canModerate = useCan('survey:moderate')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openResponseId, setOpenResponseId] = useState<string | null>(null)
  const [openBackCheck, setOpenBackCheck] = useState<SurveyBackCheckDto | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const queue = useLoad(
    () => (pollId ? getQualityQueue(pollId) : Promise.resolve(null)), [pollId])
  const backChecks = useLoad(
    () => (pollId ? listBackChecks(pollId) : Promise.resolve([])), [pollId])

  if (!canRead) return <Forbidden what="field quality" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
  }

  async function act(id: string, run: () => Promise<unknown>) {
    setBusy(id)
    setError(null)
    try {
      await run()
      queue.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  const data = queue.data
  const ruleCounts = Object.entries(data?.ruleCounts ?? {})
    .sort((a, b) => b[1] - a[1])
  const worst = ruleCounts[0]

  /**
   * One card per interviewer who appears in the queue, not one per queue row.
   *
   * The pattern a supervisor is looking for is "the same person keeps turning
   * up", and that is invisible when the record is one click behind each row.
   * Capped because each card is its own request and a wide queue would otherwise
   * open dozens.
   */
  const interviewers = Array.from(new Map(
    (data?.queue ?? [])
      .filter((item) => item.mobilizerId !== null)
      .map((item) => [item.mobilizerId!, item.mobilizerName] as const),
  ).entries())

  /** Back-checks index into the queue for a name; the DTO carries only ids. */
  const subjectOf = (responseId: string) =>
    (data?.queue ?? []).find((q) => q.responseId === responseId)?.mobilizerName ?? null

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Field quality"
        description="Flagged interviews and back-checks. A flag never removes anything — a person does, with a reason."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      {/* `backChecks.error` too: the cards below print a dash for a list that did
          not load, and a dash with no sentence beside it says nothing about why. */}
      <ErrorStrip message={error ?? queue.error ?? backChecks.error} />

      {/*
        Said once, at the top, because it is what makes this a workflow rather
        than a list of complaints — and because everything below only makes sense
        if the reader already knows a flag is a hold and not a verdict.
      */}
      <Note tone="info">
        A flag never silently removes an interview. It excludes it from the estimates by default
        and puts it here with the rule that fired, the interviewer who conducted it and — where
        the poll records audio — the recording. Every row is something to decide, and until
        somebody does, the interview is held rather than lost.
      </Note>

      {!pollId ? (
        <Note tone="info">Pick a poll to see its flagged interviews.</Note>
      ) : queue.loading && !data ? <LoadingBlock label="Loading the queue…" /> : data && (
        <>
          <KpiStrip>
            <MetricCard label="Interviews" value={count(data.interviews)} sub="countable" />
            {/*
              Two counts, no percentage. Rule 2: nothing here computes a share of
              survey data — a "23% of the sample" printed beside server-computed
              figures is a client-computed one that can disagree with them, and
              the reader cannot tell which is which.
            */}
            <MetricCard label="Flagged" value={count(data.flaggedInterviews)}
              sub={data.interviews === 0 ? 'nothing collected yet'
                : `of ${count(data.interviews)} countable`}
              accent={data.flaggedInterviews > 0} />
            <MetricCard label="Open flags" value={count(data.openFlags)} sub="awaiting a decision" />
            <MetricCard label="Most common"
              value={worst ? String(worst[1]) : '—'}
              sub={worst ? flagRuleLabel(worst[0]) : 'nothing has fired'} />
            {/*
              `?? 0` would state "0 back-checks sampled for verification" when the
              back-check read FAILED — a claim about the fieldwork that the server
              never made, and the most alarming one this card can print.
            */}
            <MetricCard label="Back-checks"
              value={backChecks.data === null ? '—' : count(backChecks.data.length)}
              sub={backChecks.data === null ? 'not loaded' : 'sampled for verification'} />
          </KpiStrip>

          {worst && data.interviews > 0 && worst[1] >= data.interviews * 0.9 && (
            <Note tone="bad">
              {flagRuleLabel(worst[0])} has fired on almost every interview. That is far more
              likely to be a mis-tuned threshold than a fraud wave — check the setting before
              working through the queue.
            </Note>
          )}

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 xl:col-span-8">
              <Panel title="Queue" description="Worst first. Each row shows what fired and why.">
                {/*
                  `ErpTable` wraps `head` in its own <tr>. Passing a fragment of
                  <Th> is the contract; a <tr> here nests one row inside another,
                  which the parser unwinds into a header that renders outside the
                  table.
                */}
                <ErpTable head={(
                  <>
                    <Th>Interviewer</Th>
                    <Th>Where</Th>
                    <Th>Submitted</Th>
                    <Th className="text-right">Duration</Th>
                    <Th>Flags</Th>
                    <Th className="text-right">Decide</Th>
                  </>
                )}>
                  {data.queue.length === 0 ? (
                    <EmptyRow colSpan={6} message={
                      'Nothing is flagged. That is the normal state of a well-run field — the '
                      + 'rules are running, and they are finding nothing.'
                    } />
                  ) : data.queue.map((item) => (
                    <tr key={item.responseId} className="hover:bg-muted/40 align-top">
                      <Td>{item.mobilizerName ?? '—'}</Td>
                      <Td className="text-muted-foreground">{item.unitName ?? '—'}</Td>
                      <Td className="text-muted-foreground">
                        {item.submittedAt ? fmtDateTime(item.submittedAt) : '—'}
                      </Td>
                      <Td className="text-right tabular-nums">{duration(item.durationSeconds)}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {item.flags.map((flag) => (
                            <span key={flag.id} title={JSON.stringify(flag.detail)}>
                              <Pill tone={flagSeverityTone(flag.severityCode)}>
                                {flagRuleLabel(flag.ruleCode)}
                              </Pill>
                            </span>
                          ))}
                        </div>
                      </Td>
                      <Td className="text-right">
                        <div className="flex justify-end gap-1">
                          {/*
                            First, not last. Accepting or rejecting an interview
                            nobody has read is the failure this screen was built
                            to end — the other two are here for the case where
                            the flag itself settles it.
                          */}
                          <button
                            type="button"
                            className="erp-btn-ghost h-8 text-erp-xs text-chocolate"
                            onClick={() => setOpenResponseId(item.responseId)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            className="erp-btn-ghost h-8 text-erp-xs text-chocolate"
                            disabled={!canModerate || busy === item.responseId}
                            title={canModerate ? 'Dismiss every open flag and put it back'
                              : 'Needs survey:moderate'}
                            onClick={() => act(item.responseId,
                              () => acceptResponse(item.responseId, 'Reviewed and accepted'))}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="erp-btn-ghost h-8 text-erp-xs text-rose-600"
                            disabled={!canModerate || busy === item.responseId}
                            title={canModerate ? 'Reject with a written reason'
                              : 'Needs survey:moderate'}
                            onClick={() => {
                              const reason = window.prompt(
                                'Why is this interview rejected? It is written to the audit log, '
                                + 'and the interview is KEPT so the response rates stay honest.')
                              if (!reason || !reason.trim()) return
                              void act(item.responseId,
                                () => rejectResponse(item.responseId, reason.trim()))
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </ErpTable>
                {/*
                  The row buttons carry their reason in a `title`, which a disabled
                  button never fires and a screen reader never reaches. Saying it
                  once in visible text is the part of rule 3 that actually lands.
                */}
                {!canModerate && (
                  <p className="mt-3 text-erp-xs text-muted-foreground">
                    Accept and Reject need <span className="font-mono">survey:moderate</span>,
                    which this account does not hold. Open still works — reading a flagged
                    interview is <span className="font-mono">survey:read</span>, and the panel
                    offers the decisions anyway so the server can say the same thing in its own
                    words.
                  </p>
                )}
              </Panel>
            </div>

            <div className="col-span-12 xl:col-span-4 space-y-4">
              <Panel title="What fired" description="Across the whole poll, not just the queue.">
                <ErpTable head={(
                  <>
                    <Th>Rule</Th>
                    <Th className="text-right">Interviews</Th>
                  </>
                )}>
                  {ruleCounts.length === 0 ? (
                    <EmptyRow colSpan={2} message="No rule has fired on this poll." />
                  ) : ruleCounts.map(([rule, n]) => (
                    <tr key={rule} className="hover:bg-muted/40">
                      <Td>{flagRuleLabel(rule)}</Td>
                      <Td className="text-right tabular-nums">{count(n)}</Td>
                    </tr>
                  ))}
                </ErpTable>
              </Panel>

              <Panel
                title="Who conducted these"
                description="Five raw counts each. Deliberately not one trust score."
                bodyClassName="space-y-3"
              >
                {interviewers.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground">
                    Nobody is in the queue, so there is no record to read.
                  </p>
                ) : (
                  <>
                    {interviewers.slice(0, INTERVIEWER_CARDS).map(([id, name]) => (
                      <InterviewerPanel key={id} mobilizerId={id} fallbackName={name} dense />
                    ))}
                    {interviewers.length > INTERVIEWER_CARDS && (
                      <p className="text-erp-xs text-muted-foreground">
                        {interviewers.length - INTERVIEWER_CARDS} more interviewer
                        {interviewers.length - INTERVIEWER_CARDS === 1 ? ' is' : 's are'} in this
                        queue. Their records are one request each and are not fetched here —
                        work the queue down, or open an interview to read the record of the
                        person who conducted it.
                      </p>
                    )}
                  </>
                )}
              </Panel>

              <Panel
                title="Back-checks"
                description="Re-contacting a sampled respondent. Detection without verification is just a list."
              >
                <ErpTable head={(
                  <>
                    <Th>Status</Th>
                    <Th>Outcome</Th>
                    <Th className="text-right">Mismatches</Th>
                    <Th className="text-right">Verify</Th>
                  </>
                )}>
                  {backChecks.data === null ? (
                    // A failed READ and an empty LIST are different facts, and
                    // "none sampled yet" asserts the second one on the evidence
                    // of the first — the same false claim the KPI above used to
                    // make with `?? 0`.
                    <EmptyRow colSpan={4} message={
                      'The back-checks could not be loaded, so nothing can be said about '
                      + 'whether any have been sampled.'
                    } />
                  ) : backChecks.data.length === 0 ? (
                    <EmptyRow colSpan={4} message={
                      'None sampled yet. The nightly job draws a stratified sample — by '
                      + 'interviewer, oversampling flagged interviews.'
                    } />
                  ) : (backChecks.data ?? []).map((check) => (
                    <tr key={check.id} className="hover:bg-muted/40 align-top">
                      <Td>
                        <div className="flex flex-col gap-0.5">
                          <Pill>{check.statusCode}</Pill>
                          <button
                            type="button"
                            className="text-erp-xs text-chocolate hover:underline text-left font-mono"
                            onClick={() => setOpenResponseId(check.responseId)}
                          >
                            {shortId(check.responseId)}
                          </button>
                        </div>
                      </Td>
                      <Td className={check.outcomeCode === 'FABRICATED' ? 'text-rose-600' : ''}>
                        {check.outcomeCode ?? '—'}
                      </Td>
                      <Td className="text-right tabular-nums">{count(check.mismatchCount)}</Td>
                      <Td className="text-right">
                        <button
                          type="button"
                          className="erp-btn-ghost h-8 text-erp-xs text-chocolate"
                          onClick={() => setOpenBackCheck(check)}
                        >
                          {check.outcomeCode ? 'Review' : 'Record'}
                        </button>
                      </Td>
                    </tr>
                  ))}
                </ErpTable>
                <div className="mt-3">
                  <Note tone="info">
                    A FABRICATED finding flags every other interview by that person on this poll
                    and puts a concern on their roster record. They are flagged and not rejected:
                    some of that person's work may well be real, and a moderator still decides
                    one at a time.
                  </Note>
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}

      {/*
        * KEYED BY THE ROW, so React remounts it instead of reusing it.
        *
        * A drawer that stays mounted across rows keeps every piece of local
        * state from the last one: a half-open form, a typed value, and — worst —
        * the previous row's error. Opening party B after a refusal on party A
        * showed A's refusal under B's name, and the "Join" button toggled a form
        * that was already open from A, so it appeared to do nothing. One line
        * here is cheaper and more reliable than an effect that resets six
        * useStates and has to be extended every time a seventh is added.
        */}
      <ResponseDrawer key={openResponseId ?? 'none'}
        responseId={openResponseId}
        onClose={() => setOpenResponseId(null)}
        onChanged={() => { queue.reload(); backChecks.reload() }}
      />

      <BackCheckDialog
        open={openBackCheck !== null}
        backCheck={openBackCheck}
        subjectLabel={openBackCheck ? subjectOf(openBackCheck.responseId) : null}
        onClose={() => setOpenBackCheck(null)}
        // A FABRICATED finding flags the interviewer's other work, so the queue
        // itself changes underneath this dialog. Reloading both is the only way
        // the consequence the dialog warned about is visible when it closes.
        onSaved={() => { backChecks.reload(); queue.reload() }}
      />
    </div>
  )
}
